import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/cn";
import { URGENCY_META, type Urgency } from "@/lib/urgency";

const alertTones = {
  info: { cls: "bg-info-tint border-info-500/30 text-info-text", Icon: Info },
  success: { cls: "bg-routine-tint border-routine-500/30 text-routine-text", Icon: CheckCircle2 },
  warning: { cls: "bg-monitor-tint border-monitor-500/30 text-monitor-text", Icon: AlertTriangle },
  danger: { cls: "bg-critical-tint border-critical-500/40 text-critical-text", Icon: ShieldAlert },
} as const;

export function Alert({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: keyof typeof alertTones;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const { cls, Icon } = alertTones[tone];
  return (
    <div
      // Assertive only for danger: an emergency banner must interrupt a screen
      // reader, an informational one must not.
      role={tone === "danger" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-xl border px-4 py-3 text-sm", cls, className)}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-bold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "leading-relaxed")}>{children}</div>}
      </div>
    </div>
  );
}

/** The banner that carries a clinical result. Always states the guidance. */
export function UrgencyPanel({
  level,
  title,
  children,
  className,
}: {
  level: Urgency;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const meta = URGENCY_META[level];
  return (
    <div
      role={level === "critical" ? "alert" : "status"}
      className={cn("overflow-hidden rounded-(--radius-card) border", meta.panel, className)}
    >
      <div className={cn("h-1", meta.bar)} aria-hidden />
      <div className="px-5 py-4">
        <div className="flex items-center gap-2">
          <span className={cn("size-2 rounded-full", meta.dot)} aria-hidden />
          <p className="text-xs font-bold tracking-wide uppercase">{meta.label}</p>
        </div>
        <h3 className="mt-1.5 text-xl">{title ?? meta.guidance}</h3>
        {children && <div className="mt-2 text-sm leading-relaxed">{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-12 text-center", className)}>
      {icon && (
        <div className="bg-surface-sunken text-content-subtle mb-3 grid size-11 place-items-center rounded-full">
          {icon}
        </div>
      )}
      <p className="font-display font-bold">{title}</p>
      {description && (
        <p className="text-content-muted mt-1 max-w-sm text-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("bg-surface-sunken animate-pulse rounded-lg", className)}
    />
  );
}

/** Full-panel loading state used while a route's data resolves. */
export function LoadingPanel({ label = "Memuat data" }: { label?: string }) {
  return (
    <div className="space-y-3 p-5" role="status" aria-label={label}>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}
